import './styles/styles.scss';

import { useMutation, useQuery } from '@apollo/client';
import { PersonRemove } from '@mui/icons-material';
import { ReportProblemOutlined } from '@mui/icons-material';
import { Grid, Paper, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import {
  DistributionStatus,
  EventWaitingType,
  ResolvedEvent,
  Enrollment
} from '@thro-types/events';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import UNENROLL_MUTATION from 'gql/mutations/EventService/unenrollFromEvent.gql';
import USERS_BY_EMAIL_QUERY from 'gql/queries/EventService/getUsersEmail.gql';
import USERS_NAMES_QUERY from 'gql/queries/EventService/getUsersNames.gql';

import { ConfirmationDialog, GQLGuard, TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { isPreCalc, removeDuplicates } from '../../helper';

interface UserQueryResponse {
  id: number;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface Props {
  eventData: ResolvedEvent;
  markedGroup: number | null;
  canEdit: boolean;
}
export const EventEnrollmentOverview = ({ eventData, markedGroup, canEdit }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user: currentUser } = useAuthContext();
  const enrollmentRows =
    eventData.groups?.find(group => group.id === markedGroup)?.enrollments || [];
  let preCalcEnrollmentRows =
    eventData.groups?.find(group => group.id === markedGroup)?.preCalcEnrollments || [];
  const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<GridRowParams<any>['row'] | null>(null);
  const [unEnroll] = useMutation(UNENROLL_MUTATION, {
    context: { clientName: 'federation' },
    refetchQueries: ['GetEvent']
  });
  const markedGroupData = eventData.groups?.find(group => group.id === markedGroup);

  const getWaitingListRows = () => {
    switch (eventData.waitingType) {
      case EventWaitingType.EVENT:
        return eventData.waitingList || null;
      case EventWaitingType.GROUP:
        return eventData.groups?.find(group => group.id === markedGroup)?.waitingList || null;
      default:
        return null;
    }
  };
  const waitingListRows = getWaitingListRows()?.enrollments?.filter(
    enrollment => !enrollment.groupId && !enrollment.preCalcGroupId
  );

  const ids = [
    ...enrollmentRows.map(row => row.userId),
    ...preCalcEnrollmentRows.map(row => row.userId),
    ...(waitingListRows?.map(enrollment => enrollment.userId) || [])
  ];

  const getUsersQuery = useQuery<
    { users: UserQueryResponse[] },
    { ids?: number[]; filter?: string; type?: UserType }
  >(canEdit ? USERS_BY_EMAIL_QUERY : USERS_NAMES_QUERY, {
    context: { clientName: 'federation' },
    variables: { ids: ids, type: UserType.SHIBBOLETH }
  });

  preCalcEnrollmentRows = removeDuplicates(
    [...enrollmentRows, ...preCalcEnrollmentRows],
    item => item.id
  );

  let enrollmentId = 0;
  if (eventData.groups) {
    for (const group of eventData.groups) {
      const enrollment =
        group.enrollments?.find(enrollment => selectedRow?.userId === enrollment.userId) ||
        group.waitingList?.enrollments?.find(
          enrollment => selectedRow?.userId === enrollment.userId
        ) ||
        group.preCalcEnrollments?.find(enrollment => selectedRow?.userId === enrollment.userId);
      if (enrollment) {
        enrollmentId = enrollment.id;
      }
    }
  }
  const enrollment = eventData.waitingList?.enrollments?.find(
    enrollment => selectedRow?.userId === enrollment.userId
  );
  if (enrollment) {
    enrollmentId = enrollment.id;
  }

  const handleUnenroll = async () => {
    try {
      await unEnroll({
        variables: {
          enrollmentId: enrollmentId
        }
      });
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.events.alerts.failedUnenroll())));
    }
  };

  const rowActions = useCallback(
    (params: GridRowParams<any>) => {
      return [
        <GridActionsCellItem
          icon={<PersonRemove sx={{ color: 'red' }} />}
          label={t(translations.common.unregister())}
          onClick={() => {
            setSelectedRow(params.row);
            setUnenrollDialogOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<
    Enrollment & { index: string; email: string; firstName: string; lastName: string }
  > => {
    return [
      {
        field: 'index',
        flex: 1,
        headerName: t(translations.pages.events.eventDetailPage.placement())
      },
      {
        field: 'userId',
        flex: 1,
        headerName: t(translations.common.userId())
      },
      {
        field: 'createdAt',
        flex: 2,
        headerName: t(translations.pages.events.eventDetailPage.timeOfEnrollment()),
        renderCell: params =>
          (params.row.userId === currentUser.id || canEdit) && (
            <Tooltip
              title={canEdit ? '' : t(translations.pages.events.tooltips.dataProtectionCreatedAt())}
            >
              <span>
                {new Date(params.row.createdAt).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric'
                })}
              </span>
            </Tooltip>
          )
      },
      {
        field: 'firstName',
        flex: 2,
        headerName: t(translations.common.firstName())
      },
      {
        field: 'lastName',
        flex: 2,
        headerName: t(translations.common.lastName())
      },
      {
        field: 'userCourse',
        flex: 2,
        headerName: t(translations.common.course()),
        renderCell: params =>
          params.row.userCourse || t(translations.common.informationUnavailable())
      },
      {
        field: 'userSemester',
        flex: 2,
        headerName: t(translations.common.semester()),
        renderCell: params =>
          params.row.userSemester || t(translations.common.informationUnavailable())
      },
      {
        field: 'email',
        flex: 2,
        headerName: t(translations.common.email())
      },
      {
        flex: 1,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [canEdit, currentUser.id, rowActions, t]);

  return (
    <Grid container className="event-enrollment-overview">
      <GQLGuard query={getUsersQuery}>
        {userData => (
          <Grid className="event-enrollment-overview__tables">
            <Grid container spacing={2}>
              {markedGroup && (
                <>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item>
                        <Typography component="h2">{`${t(
                          translations.pages.events.create.groupCard.group()
                        )} ${
                          (eventData.groups?.findIndex(group => group.id === markedGroup) || 0) + 1
                        }`}</Typography>
                      </Grid>
                      {isPreCalc(eventData) && (
                        <Grid item>
                          <Tooltip title={t(translations.pages.events.preCalculated())}>
                            <ReportProblemOutlined fontSize="large" className="alert_icon" />
                          </Tooltip>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography component="h3">
                          {t(translations.pages.events.create.groupCard.participants())}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid
                          item
                          flexGrow={1}
                          style={
                            (!!enrollmentRows.length && !isPreCalc(eventData)) ||
                            (!!preCalcEnrollmentRows.length &&
                              eventData.distributionStatus === DistributionStatus.NOT_DISTRIBUTED)
                              ? { height: '60vh', width: '100%' }
                              : { height: '25vh', width: '100%' }
                          }
                          component={Paper}
                        >
                          <DataGrid
                            style={{ height: '100%', width: '100%' }}
                            columns={columns}
                            rows={
                              isPreCalc(eventData)
                                ? preCalcEnrollmentRows.map((row, index) => ({
                                    ...row,
                                    index: index + 1,
                                    email:
                                      userData.users.find(user => user.id === row.userId)?.email ??
                                      '',
                                    firstName:
                                      userData.users.find(user => user.id === row.userId)
                                        ?.firstName ?? '',
                                    lastName:
                                      userData.users.find(user => user.id === row.userId)
                                        ?.lastName ?? ''
                                  }))
                                : enrollmentRows.map((row, index) => ({
                                    ...row,
                                    index: index + 1,
                                    email:
                                      userData.users.find(user => user.id === row.userId)?.email ??
                                      '',
                                    firstName:
                                      userData.users.find(user => user.id === row.userId)
                                        ?.firstName ?? '',
                                    lastName:
                                      userData.users.find(user => user.id === row.userId)
                                        ?.lastName ?? ''
                                  }))
                            }
                            paginationMode={'client'}
                            columnVisibilityModel={{
                              actions: canEdit && !markedGroupData?.isCancelled,
                              email: canEdit
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              {/* Warteliste */}
              <Grid item xs={12}>
                {waitingListRows !== undefined && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography component="h3">
                        {t(translations.pages.events.create.waitingList())}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid
                        item
                        flexGrow={1}
                        style={{ height: '60vh', width: '100%' }}
                        component={Paper}
                      >
                        <DataGrid
                          style={{ height: '100%', width: '100%' }}
                          columns={columns}
                          rows={
                            waitingListRows?.map((row, index) => ({
                              ...row,
                              index: index + 1,
                              email:
                                userData.users.find(user => user.id === row.userId)?.email ?? '',
                              firstName:
                                userData.users.find(user => user.id === row.userId)?.firstName ??
                                '',
                              lastName:
                                userData.users.find(user => user.id === row.userId)?.lastName ?? ''
                            })) || []
                          }
                          paginationMode={'client'}
                          columnVisibilityModel={{
                            actions: canEdit && !markedGroupData?.isCancelled,
                            email: canEdit
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </GQLGuard>
      <ConfirmationDialog
        open={unenrollDialogOpen}
        title={t(translations.pages.events.confirmationDialog.unenrollUserTitle())}
        onClose={() => {
          setUnenrollDialogOpen(false);
        }}
        onConfirm={() => {
          handleUnenroll();
          setUnenrollDialogOpen(false);
        }}
      >
        {t(translations.pages.events.confirmationDialog.unenrollUserText())}
      </ConfirmationDialog>
    </Grid>
  );
};
